import apiClient from '@/lib/api-client';
import type {
  AdminBlockClubResponse,
  AdminClubDeletionPreviewResponse,
  AdminClubsResponse,
  AdminCreateClubRequest,
  AdminCreateClubResponse,
  AdminDeleteClubResponse,
  UpdateClubRequest,
  SearchClubsRequest,
  ClubResponse,
  ClubsResponse,
  ClubModulesResponse,
  ClubOperationalPreferencesResponse,
  RoleCapabilityGridResponse,
  UpdateClubModulesRequest,
  UpdateClubOperationalPreferencesRequest,
} from '@/types/club';
import type { MemberRole } from '@/types/club-member';
import type { ClubCapability } from '@/types/club-membership';

export const clubService = {
  /**
   * Récupérer le club de l'utilisateur connecté
   */
  async getMyClub(): Promise<ClubResponse> {
    const response = await apiClient.get<ClubResponse>('/clubs/me');
    return response.data;
  },

  /**
   * Récupérer un club par son ID
   */
  async getClubById(clubId: string): Promise<ClubResponse> {
    const response = await apiClient.get<ClubResponse>(`/clubs/${clubId}`);
    return response.data;
  },

  /**
   * Mettre à jour le club de l'utilisateur connecté
   */
  async updateMyClub(data: UpdateClubRequest): Promise<ClubResponse> {
    const response = await apiClient.put<ClubResponse>('/clubs/me', data);
    return response.data;
  },

  /**
   * Récupérer les préférences opérationnelles du club connecté
   */
  async getMyOperationalPreferences(): Promise<ClubOperationalPreferencesResponse> {
    const response = await apiClient.get<ClubOperationalPreferencesResponse>('/clubs/me/preferences');
    return response.data;
  },

  /**
   * Mettre à jour les préférences opérationnelles du club connecté
   */
  async updateMyOperationalPreferences(
    data: UpdateClubOperationalPreferencesRequest,
  ): Promise<ClubOperationalPreferencesResponse> {
    const response = await apiClient.put<ClubOperationalPreferencesResponse>('/clubs/me/preferences', data);
    return response.data;
  },

  /**
   * Récupérer les modules activés du club connecté
   */
  async getMyModules(): Promise<ClubModulesResponse> {
    const response = await apiClient.get<ClubModulesResponse>('/clubs/me/modules');
    return response.data;
  },

  /**
   * Activer ou désactiver des modules du club connecté
   */
  async updateMyModules(data: UpdateClubModulesRequest): Promise<ClubModulesResponse> {
    const response = await apiClient.put<ClubModulesResponse>('/clubs/me/modules', data);
    return response.data;
  },

  /**
   * Grille des droits par rôle du club connecté
   */
  async getMyRoleCapabilities(): Promise<RoleCapabilityGridResponse> {
    const response = await apiClient.get<RoleCapabilityGridResponse>('/clubs/me/roles');
    return response.data;
  },

  /**
   * Régler les droits d'un rôle, ou les remettre à leur valeur d'origine.
   *
   * `capabilities` à `null` efface le réglage du club pour ce rôle.
   */
  async updateMyRoleCapabilities(
    role: MemberRole,
    capabilities: ClubCapability[] | null
  ): Promise<RoleCapabilityGridResponse> {
    const response = await apiClient.put<RoleCapabilityGridResponse>(`/clubs/me/roles/${role}`, {
      capabilities,
    });
    return response.data;
  },

  /**
   * Lister les clubs — administration de la plateforme
   */
  async adminListClubs(): Promise<AdminClubsResponse> {
    const response = await apiClient.get<AdminClubsResponse>('/clubs/admin');
    return response.data;
  },

  /**
   * Créer un compte club — administration de la plateforme
   */
  async adminCreateClub(data: AdminCreateClubRequest): Promise<AdminCreateClubResponse> {
    const response = await apiClient.post<AdminCreateClubResponse>('/clubs/admin', data);
    return response.data;
  },

  /**
   * Bloquer un club — administration de la plateforme
   */
  async adminBlockClub(clubId: string, reason?: string): Promise<AdminBlockClubResponse> {
    const response = await apiClient.post<AdminBlockClubResponse>(
      `/clubs/admin/${clubId}/block`,
      { reason }
    );
    return response.data;
  },

  /**
   * Débloquer un club — administration de la plateforme
   */
  async adminUnblockClub(clubId: string): Promise<AdminBlockClubResponse> {
    const response = await apiClient.post<AdminBlockClubResponse>(
      `/clubs/admin/${clubId}/unblock`
    );
    return response.data;
  },

  /**
   * Décompte de ce qu'une suppression emporterait — administration
   */
  async adminClubDeletionPreview(clubId: string): Promise<AdminClubDeletionPreviewResponse> {
    const response = await apiClient.get<AdminClubDeletionPreviewResponse>(
      `/clubs/admin/${clubId}/deletion-preview`
    );
    return response.data;
  },

  /**
   * Supprimer un club et ses données — administration de la plateforme
   *
   * Le nom du club voyage dans le corps de la requête : le serveur refait la
   * vérification, de sorte qu'une confirmation contournée côté navigateur ne
   * suffise pas à effacer un club.
   */
  async adminDeleteClub(clubId: string, confirmName: string): Promise<AdminDeleteClubResponse> {
    const response = await apiClient.delete<AdminDeleteClubResponse>(`/clubs/admin/${clubId}`, {
      data: { confirm_name: confirmName },
    });
    return response.data;
  },

  /**
   * Rechercher des clubs
   */
  async searchClubs(filters: SearchClubsRequest): Promise<ClubsResponse> {
    const response = await apiClient.get<ClubsResponse>('/clubs/search', {
      params: filters,
    });
    return response.data;
  },
};
